import React, { createRef } from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import _ from "underscore";
import PropTypes from "prop-types";
import { ReviewsSort } from "rtr-constants";
import { productPropType, userReviewSummaryPropType } from "components/propTypes";
import { isAdmin, userDataPropType, withUserData } from "components/source/hoc/with-user-data";
import { hasRented, withUserReviewSummary } from "components/layout/UserReviewSummaryContext";
import Review from "./review";
import ReviewsPagination from "./reviews-pagination";
import ReviewsHeader from "./reviews-header";
import reviewsActions from "actions/reviews-actions";
import { initialWLMFormValues } from "helpers/reviews-helpers";

export class ReviewsComponent extends React.Component {
  static propTypes = {
    count: PropTypes.number,
    coverPhotos: PropTypes.array,
    data: PropTypes.arrayOf(
      PropTypes.shape({
        moment: PropTypes.object,
      })
    ),
    dispatch: PropTypes.func.isRequired,
    page: PropTypes.number,
    perPage: PropTypes.number,
    product: productPropType,
    shouldLoadReviews: PropTypes.bool,
    sort: PropTypes.string,
    summary: PropTypes.object,
    userData: userDataPropType,
    userReviewSummary: userReviewSummaryPropType,
    userType: PropTypes.object,
    writingReview: PropTypes.bool,
    filteredCount: PropTypes.number,
  };

  constructor(props) {
    super(props);

    this.componentWrapperRef = createRef();
  }

  // I would prefer to move this to either a Context or the Redux Store and do
  // the data-fetching at a higher level. I'm not looking to re-write all of
  // this now, so we've got a bit of a hybrid
  state = {
    womenLikeMeFilter: { usStandardSize: void 0, heightInches: void 0, bustSize: void 0, birthday: void 0 },
    selectedSizes: [],
    page: 1,
    sort: null,
  };

  componentDidMount() {
    const {
      data,
      page,
      shouldLoadReviews,
      sort = ReviewsSort.WOMEN_LIKE_ME,
      userData: { userProfile } = {},
    } = this.props;

    this.setState({ page: page, sort: sort });

    if (this.props.writingReview && this.isReviewableProduct()) {
      this.triggerReviewModal();
    }

    // remove guard and fetch once on Storefront.Next
    if (!shouldLoadReviews) return;

    if (!Array.isArray(data) || data.length <= 0) {
      // default the drop-downs with userData
      const wlmFilters = initialWLMFormValues(this.state, userProfile);

      this.setWLMFilter(wlmFilters);

      // should probably set sort to Most Popular when userProfile is blank
      const params = { page, sort };

      if (sort === ReviewsSort.WOMEN_LIKE_ME) {
        params.women_like_me = wlmFilters;
      }

      this.requestReviews(params, "initial-load");
      this.requestReviewsCoverPhotos({});
    }
  }
  // Uncomment this if we need super-bar sizes transferred to anywhere in PDP - possibly upcoming feature
  // getSelectedSizesAsRegional = () => {
  //   const { product, workingFilters } = this.props;
  //   return mySelectedCanonicalSizesAsGivenProductsRegionalSizes(
  //     workingFilters?.canonicalSizes,
  //     product?.sizeScale?.canonicalToRegional
  //   );
  // };

  getWLMFilter = () => {
    const {
      womenLikeMeFilter: { usStandardSize = "", heightInches = "", bustSize = "", birthday = "" },
    } = this.state;

    return { usStandardSize, heightInches, bustSize, birthday };
  };

  filterReviewsBySize = selectedSizes => {
    if (this.state.page > 1) {
      this.setState({
        selectedSizes: selectedSizes,
      });
      this.changePage(1, "changePageSizeFilterApplied", selectedSizes);
    } else {
      const params = { sort: this.state.sort, page: this.state.page, sizes: selectedSizes };
      this.requestReviews(params, "size");
      this.setState({
        selectedSizes: selectedSizes,
      });
    }
  };

  sortReviews = e => {
    const filter = e.target.value;
    const params = { sort: filter, page: this.state.page, sizes: this.state.selectedSizes };
    this.requestReviews(params, "sort");
    this.setState({
      sort: filter,
    });
  };

  setWLMFilter = womenLikeMeFilter => {
    this.setState({ womenLikeMeFilter });
  };

  isReviewableProduct() {
    const { userData, userReviewSummary } = this.props;

    return hasRented(userReviewSummary, this.getStyleName()) || isAdmin(userData);
  }

  appendPageUrl = page => {
    if (history?.replaceState) {
      let path = window.location.pathname.replace(/\/review-\S*/, "");
      if (page > 1) {
        path += "/review-p" + page;
      }
      history.replaceState(page, "review-page", path);
    }
  };

  changePage = (page, logText = "pagination", selectedSizes = null) => {
    const params = { page: page, sort: this.props.sort, sizes: selectedSizes || this.state.selectedSizes };

    this.setState({ page: page });

    if (this.props.sort === ReviewsSort.WOMEN_LIKE_ME) {
      params.women_like_me = this.state.womenLikeMeFilter;
    }
    this.requestReviews(params, logText);

    this.componentWrapperRef.current.scrollIntoView();

    this.appendPageUrl(page);
  };

  requestReviews = (params, loggingContext) => {
    this.props.dispatch(reviewsActions.requestReviews(this.getStyleName(), params, loggingContext));
  };

  requestReviewsCoverPhotos = params => {
    this.props.dispatch(reviewsActions.requestReviewsCoverPhotos(this.getStyleName(), params));
  };

  triggerReviewModal = e => {
    if (e) {
      e.preventDefault();
    }

    const popupSource = e ? "pdp-banner" : "url-driven";
    this.props.dispatch(reviewsActions.displayReviewUploadModal({ styleName: this.getStyleName(), popupSource }));
  };

  submitComment = (reviewId, commentText) => {
    this.props.dispatch(reviewsActions.submitComment(reviewId, commentText));
  };

  getStyleName = () => {
    const { product } = this.props;
    return product?.id ?? "";
  };

  render() {
    let reviewsPagination;
    const { page, filteredCount, count, perPage, sort, summary, product, userType, data } = this.props;

    // AS [EXPLANATION] 3/13/23: for improved performance count is limited to <= 200 when sort filter is set to "like me"
    // temporary fix for user experience is to limit pagination by coercing count prop passed when "like me" sort is selected
    // and count is truncated
    const paginationReviewCount = sort === ReviewsSort.WOMEN_LIKE_ME && filteredCount > 200 ? 200 : filteredCount;
    if (filteredCount > perPage) {
      reviewsPagination = (
        <ReviewsPagination
          page={this.state.page}
          pagingCallback={this.changePage}
          count={paginationReviewCount}
          perPage={perPage}
        />
      );
    }

    // NW [EXPLANATION] 1/21/20: the first cover photo id will be used to support forward/back buttons in the moment lightbox
    const firstPhotoId = this.props.coverPhotos?.[0]?.id ?? null;
    const coverPhotos = data ? data.filter(review => review?.moment?.photos) : [];

    return (
      <div className="reviews-desktop" ref={this.componentWrapperRef}>
        <ReviewsHeader
          coverPhotos={coverPhotos}
          count={count}
          page={page}
          product={product}
          requestReviews={this.requestReviews}
          summary={summary}
          sort={this.state.sort}
          styleName={this.getStyleName()}
          triggerReviewModal={this.triggerReviewModal}
          filterReviewsBySize={this.filterReviewsBySize}
          selectedSizes={this.state.selectedSizes}
          sortReviews={this.sortReviews}
        />
        <div className="reviews-column">
          <div style={{ clear: "both" }}></div>
          <div className="reviews">
            {_.map(data, review => {
              return (
                <Review
                  filters={this.getWLMFilter()}
                  firstPhotoId={firstPhotoId}
                  key={review.moment.reviewId}
                  product={product}
                  review={review}
                  sort={sort}
                  submitComment={this.submitComment}
                  userType={userType}
                />
              );
            })}
            {reviewsPagination}
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = state => ({
  filteredCount: state?.reviews?.filteredCount,
});

export default compose(withUserData, withUserReviewSummary(), connect(mapStateToProps))(ReviewsComponent);
